import React, { Fragment } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Fund } from 'src/components/Exchange/types';
import If from 'src/components/Shared/If/If';
import Phone from 'src/components/Shared/Phone/Phone';
import config from 'src/config/config';
import { setProgress as actionSetProgress } from 'src/redux/modules/progress';
import { getUncollectedSharesLookup } from 'src/redux/modules/sellUncollectedShares/selectors';
import {
  COLLEGE_FUND_NOTE_CANNOT_LINK_BANK_FORM_URL,
  COLLEGE_FUND_NOTE_PHONE_NUMBERS,
  FundNoteDistributeCollege,
  TOLL_FREE_NUMBER,
} from 'src/settings/settings';
import { scrollElementIntoFullView } from 'src/utils/dom-utils';
import { coalesce } from 'src/utils/utils';
import words from 'src/utils/words';
import ACHRedemptionFundNoteTip from './ACHRedemptionFundNoteTip';
import AddressChangePendingFundNoteTip from './AddressChangePendingFundNoteTip';
import AddressUndeliverableFundNoteTip from './AddressUndeliverableFundNoteTip';
import CheckRedemptionFundNoteTip from './CheckRedemptionFundNoteTip';
import FundNote from './FundNote';
import './FundNote.module.scss';
import PendingTransactionFundNoteTip from './PendingTransactionFundNoteTip';
import SignatureRequiredFundNoteTip from './SignatureRequiredFundNoteTip';
import UncollectedFundNoteTip from './UncollectedFundNoteTip';
import WireRedemptionFundNoteTip from './WireRedemptionFundNoteTip';

export interface CollegeDistributeFundNotesProps {
  fund: Fund;
  sellUncollectedShares;
  setProgress(n: number): void;
}
const CollegeDistributeFundNotes: React.FC<CollegeDistributeFundNotesProps> = ({
  fund,
  sellUncollectedShares,
  setProgress,
}) => {
  const uncollectedSharesLookup = useSelector(getUncollectedSharesLookup);
  const phoneNumber = coalesce(
    COLLEGE_FUND_NOTE_PHONE_NUMBERS[fund.productType],
    TOLL_FREE_NUMBER
  );
  return (
    <Fragment>
      <If test={fund.notes[FundNoteDistributeCollege.ADDRESS_CHANGE_PENDING]}>
        <FundNote
          labelSubText=""
          labelText="Address Change"
          tipHeader="Address Change Pending"
          tipMessage={
            <AddressChangePendingFundNoteTip
              fund={fund}
              phoneNumber={phoneNumber}
            />
          }
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.ADDRESS_UNDELIVERABLE]}>
        <FundNote
          labelSubText=""
          labelText="Address Undeliverable"
          tipHeader="Address Undeliverable"
          tipMessage={
            <AddressUndeliverableFundNoteTip phoneNumber={phoneNumber} />
          }
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.AK529_ACT]}>
        <FundNote
          labelSubText=""
          labelText="Distribution Unavailable"
          tipHeader="Distribution Unavailable"
          tipMessage={
            <div className="exclude">
              Distributions from the University of Alaska Portfolio are
              unavailable online since you may be eligible for a Tuition-Value
              Guarantee. Please contact an education savings specialist at
              1-800-478-0003.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.ONE_BENE_PER_DTS]}>
        <FundNote
          labelSubText=""
          labelText="One Beneficiary Per Distribution to School"
          tipHeader="One Beneficiary Per Distribution to School"
          tipMessage={
            <div className="exclude">
              Only one beneficiary can be selected per Distribution to School
              transaction. To request a distribution for this beneficiary in
              this transaction, please deselect the other beneficiary.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.BANK_NOT_LINKED]}>
        <FundNote
          labelSubText=""
          labelText="Bank Not Linked"
          tipHeader="Bank Not Linked"
          tipMessage={
            <div className="exclude bankNotLinked">
              This {words.fund} is not connected to a bank on file. To continue,{' '}
              <a
                className="exclude college-branded-link text-nowrap"
                href={`${config.url}/usis/my-accounts/profile/linked-bank-accounts`}
              >
                link this bank
              </a>{' '}
              or{' '}
              <a
                className="exclude college-branded-link text-nowrap"
                onClick={() => {
                  setProgress(1);
                  scrollElementIntoFullView('funding-target-step');
                }}
              >
                choose a different delivery method.
              </a>
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.CANNOT_LINK_BANK]}>
        <FundNote
          labelSubText="Signature Required"
          labelText="Cannot Link Bank"
          tipHeader="Cannot Link Bank"
          tipMessage={
            <SignatureRequiredFundNoteTip
              formUrl={
                COLLEGE_FUND_NOTE_CANNOT_LINK_BANK_FORM_URL[fund.productType]
              }
            />
          }
        />
      </If>
      <If
        test={
          fund.notes[FundNoteDistributeCollege.CHECK_DISTRIBUTION_UNAVAILABLE]
        }
      >
        <FundNote
          labelSubText=""
          labelText="Check Distribution Unavailable"
          tipHeader="Check Distribution Unavailable"
          tipMessage={
            <CheckRedemptionFundNoteTip fund={fund} phoneNumber={phoneNumber} />
          }
        />
      </If>
      <If
        test={fund.notes[FundNoteDistributeCollege.DIRECT_DEPOSIT_UNAVAILABLE]}
      >
        <FundNote
          labelSubText=""
          labelText="Direct Deposit Unavailable"
          tipHeader="Direct Deposit Unavailable"
          tipMessage={
            <ACHRedemptionFundNoteTip fund={fund} phoneNumber={phoneNumber} />
          }
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.STOP_TRANSFER]}>
        <FundNote
          labelSubText=""
          labelText="Stop Transfer"
          tipHeader="Stop Transfer"
          tipMessage={
            <div className="exclude">
              A stop transfer has been applied to this account. Call{' '}
              <Phone phoneNumber={phoneNumber} /> to distribute from this{' '}
              {words.fund}.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.TRANSACTION_PENDING]}>
        <FundNote
          labelSubText=""
          labelText="Transaction Pending"
          tipHeader="Transaction Pending"
          tipMessage={<PendingTransactionFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.TRANSACTION_PENDING_ALL]}>
        <FundNote
          labelSubText=""
          labelText="Transaction Pending"
          tipHeader="Transaction Pending"
          tipMessage={<PendingTransactionFundNoteTip allFunds={true} />}
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.UNAVAILABLE_ONLINE]}>
        <FundNote
          labelSubText=""
          labelText="Unavailable Online"
          tipHeader="Unavailable Online"
          tipMessage={
            <div className="exclude">
              Online/phone distribution disabled. Call{' '}
              <Phone phoneNumber={phoneNumber} /> to enable distributions.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteDistributeCollege.UNCOLLECTED_UNITS]}>
        <FundNote
          labelSubText=""
          labelText="Uncollected Units"
          tipHeader={'Uncollected Units'}
          tipMessage={
            <UncollectedFundNoteTip
              {...{ fund, sellUncollectedShares, uncollectedSharesLookup }}
            />
          }
        />
      </If>
      <If
        test={fund.notes[FundNoteDistributeCollege.WIRE_REDEMPTION_UNAVAILABLE]}
      >
        <FundNote
          labelSubText=""
          labelText={`Wire ${words.Redemption} Unavailable`}
          tipHeader={`Wire ${words.Redemption} Unavailable`}
          tipMessage={
            <WireRedemptionFundNoteTip fund={fund} phoneNumber={phoneNumber} />
          }
        />
      </If>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  const { sellUncollectedShares } = state;
  return {
    sellUncollectedShares,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setProgress: actionSetProgress,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CollegeDistributeFundNotes);
